<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApi } from '@/composables/api'
import type { PreviewIncidentReportResponse } from '@roadfootage/api-client'
import { useShare } from '@/composables/useShare'
import { formatIncidentType, formatIncidentDateTime } from '@/utils/format'

import PreviewMediaPlayer from '../components/preview-incident/PreviewMediaPlayer.vue'
import PreviewMapCard from '../components/preview-incident/PreviewMapCard.vue'
import PreviewDetailsCard from '../components/preview-incident/PreviewDetailsCard.vue'
import PreviewDescriptionCard from '../components/preview-incident/PreviewDescriptionCard.vue'
import PreviewPurchaseCard from '../components/preview-incident/PreviewPurchaseCard.vue'
const route = useRoute()
const { shareIncident } = useShare()
const router = useRouter()
// No per-call Turnstile token: App.vue holds the router-view until the Turnstile challenge has been
// exchanged for an anonymous session, and the Preview endpoint accepts that bearer token.
const { previewApi } = useApi()

const cameFromMap = window.history.state?.back?.startsWith('/explore') ?? false

const loading = ref(true)
const incidentReport = ref<PreviewIncidentReportResponse | null>(null)

const pageTitle = computed(() => {
  if (!incidentReport.value) return ''
  const type = formatIncidentType(incidentReport.value.incidentReportType)
  const location =
    incidentReport.value.address?.suburb ??
    incidentReport.value.address?.formattedAddress ??
    'Unknown location'
  return `${type} — ${location}`
})

const pageSubtitle = computed(() =>
  formatIncidentDateTime(
    incidentReport.value?.incidentDateTime,
    incidentReport.value?.incidentTimezone
  )
)

async function fetchIncidentDetails() {
  const id = route.params.incidentReportId as string
  try {
    incidentReport.value = await previewApi.previewPreviewIncidentReportById({ id })
  } catch (error) {
    console.error('Failed to fetch incident details:', error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchIncidentDetails()
})
</script>

<template>
  <q-page padding class="bg-grey-2">
    <!--
      layout-wide + q-mx-auto sits on a wrapper, NOT on the row.
      q-col-gutter-lg applies margin: -12px to the row, which cancels the 12px col
      padding at the outer edges — giving a clean 16px (page padding) outer gutter.
      Putting q-mx-auto on the row itself overrides the negative margin via !important,
      which defeats this mechanism and adds an extra 12px on each side.
    -->
    <div class="layout-wide q-mx-auto">
      <div class="row q-col-gutter-lg">
        <!-- Title block -->
        <div class="col-12">
          <!-- Main title -->
          <q-skeleton v-if="loading" type="text" width="65%" height="2rem" />
          <h1 v-else class="text-h5 text-weight-bold q-mt-none q-mb-xs">
            {{ pageTitle }}
          </h1>

          <!-- Date / time subtitle -->
          <q-skeleton v-if="loading" type="text" width="40%" height="1rem" />
          <div v-else-if="pageSubtitle" class="text-body2 text-grey-7">
            {{ pageSubtitle }}
          </div>

          <!-- Country / state chips -->
          <q-skeleton v-if="loading" type="text" width="25%" height="1.4rem" class="q-mb-xs" />
          <div
            v-else-if="incidentReport?.address?.country || incidentReport?.address?.state"
            class="row q-gutter-xs q-my-xs"
          >
            <q-chip
              v-if="incidentReport?.address?.country"
              dense
              color="grey-6"
              text-color="white"
              size="sm"
              :label="incidentReport.address.country"
            />
            <q-chip
              v-if="incidentReport?.address?.state"
              dense
              color="primary"
              text-color="white"
              size="sm"
              :label="incidentReport.address.state"
            />
            <q-chip
              dense
              color="grey-6"
              text-color="white"
              size="sm"
              :label="formatIncidentType(incidentReport?.incidentReportType)"
            />
          </div>

          <!-- Share -->
          <div v-if="!loading && incidentReport" class="q-mt-sm">
            <q-btn
              outline
              dense
              size="sm"
              icon="mdi-share-variant"
              label="Share"
              color="primary"
              data-testid="share-incident"
              @click="shareIncident(route.params.incidentReportId as string)"
            />
          </div>

          <!-- Back to map -->
          <div v-if="cameFromMap" class="q-mt-sm">
            <q-btn
              flat
              dense
              size="sm"
              icon="mdi-arrow-left"
              label="Back to map"
              color="primary"
              @click="router.back()"
            />
          </div>
        </div>

        <!-- Media player — col-8 on desktop, full width on mobile -->
        <div class="col-12 col-lg-8">
          <PreviewMediaPlayer v-if="incidentReport" :incident="incidentReport" />
          <q-card v-else>
            <q-responsive :ratio="16 / 9">
              <q-skeleton class="fit" />
            </q-responsive>
          </q-card>
        </div>

        <!-- Map sidebar — col-4 on desktop, full width on mobile -->
        <div class="col-12 col-lg-4">
          <PreviewMapCard v-if="incidentReport" :incident="incidentReport" />
          <q-card v-else>
            <q-skeleton height="300px" />
          </q-card>
        </div>

        <!-- Incident description -->
        <div class="col-12 col-lg-8">
          <PreviewDescriptionCard v-if="incidentReport" :incident="incidentReport" />
          <q-skeleton v-else type="rect" height="120px" />
        </div>

        <!-- Incident details -->
        <div class="col-12 col-lg-4">
          <PreviewDetailsCard v-if="incidentReport" :incident="incidentReport" />
          <q-skeleton v-else type="rect" height="140px" />
        </div>

        <!-- Purchase / download -->
        <div class="col-12">
          <PreviewPurchaseCard />
        </div>
      </div>
    </div>
  </q-page>
</template>
